import {
  listDispositionFlagsForWorkflow,
  listDispositionsForWorkflow,
  listSelectableDispositionsForWorkflow,
} from '@100mslive/disposition-engine';
import type { Disposition } from '@compass/client/types/disposition';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { resolveEngineWorkflowType } from '@/lib/disposition/engineWorkflowType';
import { caseRecordService } from '@/services/api';
import { queryKeys } from '@/services/query.config';
import { useStore } from '@/store/index';
import { compareDispositionLabels } from '@/utils/dispositions';

interface UseDispositionsResult {
  data: Disposition[];
  isLoading: false;
  isError: false;
  error: null;
}

/**
 * Returns the disposition catalog for a workflow_type, resolved synchronously
 * from `@100mslive/disposition-engine`.
 *
 * Replaces the legacy `dispositionService.getDispositionsByAgentId()` API call.
 * Disposition codes post-BI-864 are globally unique and shared across every
 * agent of a workflow_type — so we no longer need per-agent fetches. The
 * hook is now a pure derivation of `workflowType`; no network, no React
 * Query state. The return shape preserves `data` / `isLoading` / `error` so
 * existing callers keep working.
 *
 * @param workflowType - The case's workflow_type (e.g. "medbv", "pbm",
 *   "unifiedmedbv", "transportation"). When undefined the hook returns an
 *   empty list.
 */
export function useDispositions(workflowType: string | undefined): UseDispositionsResult {
  return useMemo(() => {
    if (!workflowType) {
      return { data: [], isLoading: false, isError: false, error: null };
    }
    let catalog: Record<number, string> = {};
    let flags: Record<number, string> = {};
    let engineType: string;
    try {
      engineType = resolveEngineWorkflowType(workflowType);
      // Display view: includes retired codes so stored rows resolve to a label.
      catalog = listDispositionsForWorkflow(engineType);
      flags = listDispositionFlagsForWorkflow(engineType);
    } catch {
      // Unknown workflow_type — return empty rather than throwing so the UI
      // degrades gracefully (legacy case records may carry a workflow_type
      // that the new catalog doesn't recognise).
      return { data: [], isLoading: false, isError: false, error: null };
    }
    const dispositions: Disposition[] = Object.entries(catalog).map(([code, label]) => ({
      code: Number(code),
      label,
      workflow_type: workflowType,
      // Carries the boolean output_data flag (e.g. "third_party_pushback") to flip
      // true on human disposition selection; undefined for dispositions without one.
      output_data_flag: flags[Number(code)],
    }));
    return { data: dispositions, isLoading: false, isError: false, error: null };
  }, [workflowType]);
}

/**
 * The subset of {@link useDispositions} that a user may actually pick.
 *
 * Identical to `useDispositions` except that retired codes
 * (`DEPRECATED_DISPOSITION_CODES`) are dropped, so nobody can newly select one.
 *
 * `currentCode` is the disposition the record is ALREADY set to. When that value
 * is itself deprecated it is kept in the list, so the dropdown shows what the
 * record holds rather than rendering blank; it still does not appear for any
 * other record. Pass it wherever the control is bound to an existing value.
 *
 * Options are sorted ascending by label (`compareDispositionLabels`), so every
 * disposition dropdown reads A→Z.
 *
 * Use this for dropdown options. Use `useDispositions` for display and
 * `code -> label` lookups — it returns the full catalog, which is what keeps
 * historical rows readable.
 *
 * @param workflowType - The case's workflow_type.
 * @param currentCode - The record's existing disposition code, if any.
 */
export function useSelectableDispositions(
  workflowType: string | undefined,
  currentCode?: number,
): UseDispositionsResult {
  const { data } = useDispositions(workflowType);
  return useMemo(() => {
    if (!workflowType) {
      return { data: [], isLoading: false, isError: false, error: null };
    }
    let selectable: Record<number, string>;
    try {
      selectable = listSelectableDispositionsForWorkflow(resolveEngineWorkflowType(workflowType));
    } catch {
      return { data: [], isLoading: false, isError: false, error: null };
    }
    return {
      data: data
        .filter(d => d.code !== undefined && (d.code in selectable || d.code === currentCode))
        .sort((a, b) => compareDispositionLabels(a.label, b.label)),
      isLoading: false,
      isError: false,
      error: null,
    };
  }, [data, workflowType, currentCode]);
}

/**
 * Hook to update disposition code for a case record
 * @param caseRecordId - Case record ID to update
 */
export function useUpdateDispositionCode(caseRecordId: string) {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: (dispositionCode: number) => {
      if (!selectedOrganizationId || !selectedProgramId) {
        throw new Error('Organization and program context required');
      }
      return caseRecordService.updateDispositionCode({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseRecordId,
        disposition_code: dispositionCode,
      });
    },
    onMutate: async (newDispositionCode: number) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.cases.byId(caseRecordId),
      });

      // Snapshot the previous value
      const previousCase = queryClient.getQueryData(queryKeys.cases.byId(caseRecordId));

      // Optimistically update the cache
      queryClient.setQueryData(queryKeys.cases.byId(caseRecordId), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          disposition_code: newDispositionCode,
        };
      });

      // Return context with previous value for rollback
      return { previousCase };
    },
    onSuccess: () => {
      // Invalidate to ensure we have the latest server state
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.byId(caseRecordId),
      });
      toast.success('Disposition updated successfully');
    },
    onError: (error: any, _variables, context) => {
      // Rollback to previous value on error
      if (context?.previousCase) {
        queryClient.setQueryData(queryKeys.cases.byId(caseRecordId), context.previousCase);
      }
      const errorMessage = error?.response?.data?.message || 'Failed to update disposition';
      toast.error(errorMessage);
    },
  });
}
